import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { isTokenExpired } from '../auth/jwt-utils';

/**
 * Interceptor funcional:
 *   1. Pré-voo: se o token guardado já está expirado (ou expira nos
 *      próximos 10s), tenta refresh via `AuthService.refreshAccessToken()`
 *      ANTES de enviar a request, e a envia já com o token novo.
 *   2. Em 401 (o pré-voo não pegou — revogação no servidor, clock skew
 *      etc.), tenta o mesmo refresh reativamente e repete a request original.
 *   3. Se o refresh falhar (ou não houver refresh token) em qualquer um dos
 *      dois casos, desloga e manda pra `/login` — mesma rede de segurança
 *      que existia antes.
 *
 * Múltiplas requests que precisam de refresh compartilham um único refresh
 * em vôo (cf. `AuthService.refreshAccessToken` com `share()`).
 */
let isRedirecting = false;

const withBearer = (req: HttpRequest<any>, token: string): HttpRequest<any> =>
  req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

function forceLogout(authService: AuthService, error?: HttpErrorResponse): Observable<never> {
  if (!isRedirecting) {
    isRedirecting = true;
    authService.logout();
    setTimeout(() => { isRedirecting = false; }, 2000);
  }
  return throwError(() => error ?? new Error('Sessão expirada.'));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  const isAssetRequest = req.url.includes('/assets/') || req.url.includes('/i18n/');
  if (isAssetRequest) {
    return next(req);
  }

  const currentUrl = router.url;
  const isAlreadyOnLoginPage = currentUrl.includes('/login') || currentUrl.includes('/signin');
  const isAuthRequest =
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/refresh-token') ||
    req.url.includes('/auth/forgot-password') ||
    req.url.includes('/auth/new-password');
  const bypassesTokenHandling = isAuthRequest || isAlreadyOnLoginPage;

  const forward = (outgoing: HttpRequest<any>) =>
    next(outgoing).pipe(
      catchError((error: HttpErrorResponse) => {
        if (bypassesTokenHandling || error.status !== 401) {
          return throwError(() => error);
        }

        const refreshToken = authService.getRefreshToken();
        if (!refreshToken) {
          return forceLogout(authService, error);
        }

        return authService.refreshAccessToken().pipe(
          switchMap((newToken) => next(withBearer(outgoing, newToken))),
          catchError(() => forceLogout(authService, error))
        );
      })
    );

  if (!bypassesTokenHandling) {
    const token = authService.getToken();
    if (token && isTokenExpired(token)) {
      const refreshToken = authService.getRefreshToken();
      if (!refreshToken) {
        return forceLogout(authService);
      }

      return authService.refreshAccessToken().pipe(
        catchError(() => forceLogout(authService)),
        switchMap((newToken) => forward(withBearer(req, newToken)))
      );
    }
  }

  return forward(req);
};
